/**
 * MockSession — Full-screen interview room.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────┐
 *   │  TOP BAR: Logo · Stage · Progress · Timer · End│
 *   ├────────────────────────────────┬───────────────┤
 *   │                                │  Interviewer  │
 *   │   CHAT (scrollable)            │  video (top)  │
 *   │                                ├───────────────┤
 *   │   Resume / Hint / Speaking     │  Your webcam  │
 *   │   ─────────────────────────    │  (bottom)     │
 *   │   Input | Mic | 💻 | Send      │               │
 *   └────────────────────────────────┴───────────────┘
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Editor from "@monaco-editor/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { api } from "@/lib/api";
import { toast } from "sonner";
import fixWebmDuration from "fix-webm-duration";
import LiveAvatarVideo from "@/components/LiveAvatarVideo";

const STAGE_LABELS = {
  greeting: "Welcome", warmup: "Getting to Know You", resume_collection: "Resume Upload",
  intro: "Introduction", project_deep_dive: "Project Deep-Dive",
  technical_core: "Technical — Core", scenario_based: "Real-World Scenarios",
  technical_secondary: "Technical — Advanced",
  coding_round: "Coding Challenge", wrap_up: "Wrap Up", completed: "Completed",
};
const STAGE_ORDER = [
  "greeting","warmup","resume_collection","intro","project_deep_dive",
  "technical_core","scenario_based","technical_secondary","coding_round","wrap_up","completed",
];

// ── Stage progress bar ────────────────────────────────────────────────────────
function StageProgress({ current }) {
  const idx = STAGE_ORDER.indexOf(current);
  return (
    <div style={{ display: "flex", gap: 3, alignItems: "center", flex: 1 }}>
      {STAGE_ORDER.filter((s) => s !== "completed").map((s, i) => (
        <div key={s} title={STAGE_LABELS[s]} style={{
          flex: 1, height: 3, borderRadius: 2,
          background: i < idx ? "#e63946" : s === current ? "rgba(230,57,70,0.55)" : "rgba(255,255,255,0.08)",
          transition: "background 0.4s",
        }} />
      ))}
    </div>
  );
}

// ── Lock timer ────────────────────────────────────────────────────────────────
function SessionTimer({ startedAt, lockExpiresAt, maxExpiresAt, onLockRelease }) {
  const [now, setNow] = useState(Date.now());
  const released      = useRef(false);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  // All timestamps from backend now end with "Z" (UTC) — parsed correctly by JS
  const startTs = new Date(startedAt).getTime();
  const lockTs  = new Date(lockExpiresAt).getTime();
  const maxTs   = new Date(maxExpiresAt).getTime();

  const locked    = lockTs - now > 0;
  const elapsedSec = Math.max(0, Math.floor((now - startTs) / 1000));
  const remainSec  = Math.max(0, Math.floor((maxTs - now) / 1000));

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  useEffect(() => { if (!locked && !released.current) { released.current = true; onLockRelease?.(); } }, [locked]);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
      {/* Elapsed — counts up */}
      <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, padding: "3px 9px", fontSize: 11, color: "rgba(255,255,255,0.55)", fontFamily: "monospace", fontWeight: 700 }}>
        ⏱ {fmt(elapsedSec)}
      </div>
      {/* Lock / max remaining */}
      {locked
        ? <div style={{ background: "rgba(230,57,70,0.1)", border: "1px solid rgba(230,57,70,0.3)", borderRadius: 7, padding: "3px 9px", fontSize: 11, color: "#e63946", fontFamily: "monospace", fontWeight: 700 }}>🔒 {fmt(remainSec)} left</div>
        : <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 7, padding: "3px 9px", fontSize: 11, color: "#22c55e", fontFamily: "monospace", fontWeight: 700 }}>✅ {fmt(remainSec)} left</div>
      }
    </div>
  );
}

// ── Interviewer video panel ───────────────────────────────────────────────────
function InterviewerPanel({ avatarRef, sessionId, gender, name, fallbackSrc, role,
                            speaking, ready, onConnected, onSpeakStart, onSpeakEnd }) {
  const [avatarStarted, setAvatarStarted] = useState(false);

  return (
    <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "transparent", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>

      {/* Avatar — only mounted after user clicks Start */}
      {avatarStarted ? (
        <LiveAvatarVideo
          ref={avatarRef}
          startFn={() => api.mockAvatarStart(sessionId)}
          gender={gender} lang="en" topic="mock"
          fallbackSrc={fallbackSrc} fallbackAlt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", position: "relative", zIndex: 1 }}
          onStatusChange={(s) => { if (s === "connected") onConnected(); }}
          onSpeakStart={onSpeakStart}
          onSpeakEnd={onSpeakEnd}
        />
      ) : (
        /* ── Pre-start state: static image + Start button ── */
        <>
          <img
            src={fallbackSrc} alt={name}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", position: "relative", zIndex: 1 }}
          />
          {/* Start button overlay */}
          <div style={{ position: "absolute", inset: 0, zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: "rgba(255,255,255,0.8)", letterSpacing: "0.04em" }}>{name}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: -8 }}>{role}</div>
            <button
              onClick={() => setAvatarStarted(true)}
              style={{
                marginTop: 6,
                display: "flex", alignItems: "center", gap: 8,
                background: "linear-gradient(135deg,#e63946,#c1121f)",
                color: "#fff", border: "none", borderRadius: 12,
                padding: "10px 22px", fontSize: 13, fontWeight: 700,
                cursor: "pointer", letterSpacing: "0.04em",
                boxShadow: "0 4px 24px rgba(230,57,70,0.5)",
                transition: "transform 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
            >
              <span style={{ fontSize: 16 }}>▶</span> Start Live Avatar
            </button>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", textAlign: "center", maxWidth: 140, lineHeight: 1.5 }}>
              Click when ready to activate the AI interviewer
            </div>
          </div>
        </>
      )}

      {/* Label overlay */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 5,
        padding: "18px 12px 8px",
      }}>
        <div style={{ fontWeight: 800, fontSize: 13, color: "#fff" }}>{name}</div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 1 }}>{role}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%",
            background: speaking ? "#e63946" : ready ? "#22c55e" : avatarStarted ? "#fbbf24" : "rgba(255,255,255,0.2)",
            boxShadow: speaking ? "0 0 7px #e63946" : "none" }} />
          <span style={{ fontSize: 10, fontWeight: 600,
            color: speaking ? "#e63946" : ready ? "#22c55e" : avatarStarted ? "#fbbf24" : "rgba(255,255,255,0.3)" }}>
            {speaking ? "Speaking" : ready ? "Listening" : avatarStarted ? "Connecting…" : "Click ▶ to start avatar"}
          </span>
          {/* Mini waveform */}
          {speaking && (
            <div style={{ display: "flex", gap: 2, alignItems: "center", height: 14 }}>
              {[1,0.5,0.8,0.4,0.9,0.6,1,0.5].map((h, i) => (
                <div key={i} style={{ width: 2, borderRadius: 1, background: "#e63946",
                  height: `${Math.round(h*12)}px`,
                  animation: `wave ${0.5+(i%4)*0.1}s ease-in-out ${i*0.05}s infinite alternate` }} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── User webcam panel ─────────────────────────────────────────────────────────
// Receives the already-acquired recording stream from parent — no second getUserMedia call.
function UserCamPanel({ stream }) {
  const videoRef = useRef(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!stream) return;
    const vid = videoRef.current;
    if (!vid) return;
    vid.srcObject = stream;
    vid.onloadedmetadata = () => setOk(true);
    vid.oncanplay = () => setOk(true); // canvas streams may not fire onloadedmetadata
    vid.play().catch(() => {});
  }, [stream]);

  return (
    <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#050818" }}>
      {/* Always render the video element — becomes visible once srcObject is set */}
      <video
        ref={videoRef}
        autoPlay playsInline muted
        style={{
          width: "100%", height: "100%", objectFit: "cover",
          transform: "scaleX(-1)",
          display: ok ? "block" : "none",
        }}
      />
      {/* Placeholder while stream loads */}
      {!ok && (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "rgba(255,255,255,0.25)" }}>
          <span style={{ fontSize: 32 }}>{stream ? "⏳" : "📵"}</span>
          <span style={{ fontSize: 11 }}>{stream ? "Connecting camera…" : "Requesting camera…"}</span>
        </div>
      )}
      {/* Label */}
      <div style={{
        position: "absolute", bottom: 8, left: 8, zIndex: 5,
        background: "rgba(6,10,31,0.85)", borderRadius: 6, padding: "3px 9px",
        display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.75)",
      }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%",
          background: ok ? "#e63946" : "rgba(255,255,255,0.2)",
          boxShadow: ok ? "0 0 5px #e63946" : "none", display: "inline-block" }} />
        You · {ok ? "REC" : stream ? "…" : "Off"}
      </div>
    </div>
  );
}

// ── Chat bubble ───────────────────────────────────────────────────────────────
const mdComponents = {
  // Inline code
  code({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    if (!inline && match) {
      return (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          customStyle={{
            margin: "10px 0", borderRadius: 8, fontSize: 12.5,
            border: "1px solid rgba(255,255,255,0.1)",
          }}
          {...props}
        >
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      );
    }
    // Inline code pill
    return (
      <code style={{
        background: "rgba(255,255,255,0.1)", borderRadius: 4,
        padding: "1px 6px", fontSize: "0.88em", fontFamily: "monospace",
        color: "#a5b4fc",
      }} {...props}>
        {children}
      </code>
    );
  },
  // Paragraphs — remove extra margin
  p({ children }) { return <p style={{ margin: "4px 0", lineHeight: 1.65 }}>{children}</p>; },
  // Bold
  strong({ children }) { return <strong style={{ color: "#f1f1f1", fontWeight: 700 }}>{children}</strong>; },
  // Lists
  ul({ children }) { return <ul style={{ paddingLeft: 18, margin: "6px 0" }}>{children}</ul>; },
  ol({ children }) { return <ol style={{ paddingLeft: 18, margin: "6px 0" }}>{children}</ol>; },
  li({ children }) { return <li style={{ margin: "3px 0", lineHeight: 1.6 }}>{children}</li>; },
  // Block quote
  blockquote({ children }) {
    return <blockquote style={{ borderLeft: "3px solid #e63946", paddingLeft: 10, margin: "6px 0", color: "rgba(255,255,255,0.6)", fontStyle: "italic" }}>{children}</blockquote>;
  },
};

function ChatBubble({ msg }) {
  const isAI = msg.role === "interviewer";
  return (
    <div style={{ display: "flex", justifyContent: isAI ? "flex-start" : "flex-end", marginBottom: 10 }}>
      <div style={{
        maxWidth: "86%",
        background: isAI ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg,rgba(230,57,70,0.22),rgba(193,18,31,0.22))",
        border: isAI ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(230,57,70,0.3)",
        borderRadius: isAI ? "3px 14px 14px 14px" : "14px 3px 14px 14px",
        padding: "9px 13px", color: "#f1f1f1", fontSize: 13.5,
      }}>
        {isAI && (
          <div style={{ color: "#e63946", fontSize: 10, fontWeight: 800, letterSpacing: 1, marginBottom: 5 }}>
            INTERVIEWER
          </div>
        )}
        {isAI ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {msg.content}
          </ReactMarkdown>
        ) : (
          <p style={{ margin: 0, lineHeight: 1.65 }}>{msg.content}</p>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function MockSession() {
  const { sessionId } = useParams();
  const navigate      = useNavigate();

  const [session,      setSession]      = useState(null);
  const [messages,     setMessages]     = useState([]);
  const [stage,        setStage]        = useState("greeting");
  const [input,        setInput]        = useState("");
  const [sending,      setSending]      = useState(false);
  const [lockReleased, setLockReleased] = useState(false);
  const [ending,       setEnding]       = useState(false);

  const avatarRef        = useRef(null);
  const [avatarSpeaking, setAvatarSpeaking] = useState(false);
  const [avatarReady,    setAvatarReady]    = useState(false);

  const [resumeUploaded, setResumeUploaded] = useState(false);
  const resumeInputRef   = useRef(null);
  const [uploading,      setUploading]      = useState(false);

  const [code,       setCode]       = useState("# Write your solution here\n");
  const [language,   setLanguage]   = useState("python");
  const [showCode,   setShowCode]   = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [listening,      setListening]      = useState(false);
  const [hintMode,       setHintMode]       = useState(false);
  const [silencePrompt,  setSilencePrompt]  = useState(false); // "are you there?" nudge
  const speechRef        = useRef(null);
  const silenceRef       = useRef(null);
  const lastActivityRef  = useRef(Date.now()); // updated on every user action

  // Camera stream as STATE so UserCamPanel gets it reactively
  const [camStream, setCamStream] = useState(null);
  const mrRef        = useRef(null);
  const chunksRef    = useRef([]);
  const recStartRef  = useRef(null);  // timestamp (ms) when MediaRecorder.start() was called

  const chatEndRef = useRef(null);

  // ── Load session ─────────────────────────────────────────────────────────────
  useEffect(() => {
    api.mockSessionGet(sessionId)
      .then((s) => {
        setSession(s);
        setMessages(s.transcript || []);
        setStage(s.stage);
        setResumeUploaded(!!(s.resume_s3_key || s.resume_text));
        if (s.stage === "coding_round") setShowCode(true);
        if (s.stage === "completed") navigate(`/mock/feedback/${sessionId}`, { replace: true });
      })
      .catch(() => toast.error("Session not found."));
  }, [sessionId]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { if (stage === "coding_round") setShowCode(true); }, [stage]);

  // ── Silence detection — prompt user after 30 s of inactivity ─────────────────
  useEffect(() => {
    const SILENCE_MS = 30_000;
    const t = setInterval(() => {
      // Skip if interviewer is talking, we're waiting for a response, or stage is inactive
      if (stage === "completed" || stage === "resume_collection") return;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= SILENCE_MS && !silencePrompt) setSilencePrompt(true);
    }, 5000);
    return () => clearInterval(t);
  }, [stage, silencePrompt]);

  // ── Auto-mic: start listening the moment it becomes the user's turn ───────────
  // Case 1: avatar was speaking and just stopped → start mic
  const wasAvatarSpeakingRef = useRef(false);
  useEffect(() => {
    const wasSpeaking = wasAvatarSpeakingRef.current;
    wasAvatarSpeakingRef.current = avatarSpeaking;
    if (wasSpeaking && !avatarSpeaking && !sending && !listening &&
        stage !== "completed" && stage !== "resume_collection") {
      const t = setTimeout(() => startVoice(true), 650);
      return () => clearTimeout(t);
    }
  }, [avatarSpeaking]); // eslint-disable-line

  // Case 2: no avatar / avatar skipped → start mic when a new interviewer message arrives
  const prevMsgLenRef = useRef(0);
  useEffect(() => {
    const len = messages.length;
    const prev = prevMsgLenRef.current;
    prevMsgLenRef.current = len;
    if (len > prev && len > 0) {
      const last = messages[len - 1];
      if (last?.role === "interviewer" && !avatarSpeaking && !sending && !listening &&
          stage !== "completed" && stage !== "resume_collection") {
        const t = setTimeout(() => startVoice(true), 800);
        return () => clearTimeout(t);
      }
    }
  }, [messages]); // eslint-disable-line

  // ── Block back nav during lock ────────────────────────────────────────────────
  useEffect(() => {
    if (lockReleased) return;
    window.history.pushState(null, "", window.location.pathname);
    const h = () => { window.history.pushState(null, "", window.location.pathname); toast.error("Cannot leave during the first 30 minutes"); };
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, [lockReleased]);

  useEffect(() => {
    if (lockReleased) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [lockReleased]);

  // ── Start camera + recording (raw stream, no virtual background) ──────────────
  useEffect(() => {
    async function startRec() {
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: {
            echoCancellation: true,
            noiseSuppression:  true,
            autoGainControl:   true,
            sampleRate:        48000,
          },
        });
      } catch {
        try { stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false }); } catch { return; }
      }

      setCamStream(stream);

      const hasAudio = stream.getAudioTracks().length > 0;
      if (hasAudio) {
        try {
          const mr = new MediaRecorder(stream, { mimeType: "video/webm;codecs=vp8,opus" });
          mr.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
          recStartRef.current = Date.now();
          mr.start(5000);
          mrRef.current = mr;
        } catch { /* recording unavailable */ }
      }
    }
    startRec();
    return () => {
      try { if (mrRef.current?.state !== "inactive") mrRef.current?.stop(); } catch {}
    };
  }, []);

  async function stopRec(upload = true) {
    camStream?.getTracks().forEach((t) => t.stop());

    const mr = mrRef.current;
    if (!mr || mr.state === "inactive") return;

    // mr.stop() is async: the final chunk arrives via ondataavailable AFTER stop()
    // completes, then onstop fires. We must wait for onstop before building the blob,
    // otherwise the upload gets an incomplete file (or nothing at all).
    await new Promise((resolve) => {
      mr.onstop = async () => {
        if (upload && chunksRef.current.length > 0) {
          const rawBlob = new Blob(chunksRef.current, { type: "video/webm" });
          try {
            // Fix the WebM duration metadata — MediaRecorder with timeslice writes
            // only the first chunk's duration into the header, making the browser
            // think the video is 2–5 seconds long even for a 30-minute session.
            const durationMs = recStartRef.current ? Date.now() - recStartRef.current : 0;
            const blob = durationMs > 0
              ? await fixWebmDuration(rawBlob, durationMs)
              : rawBlob;
            await api.mockSessionUploadRecording(sessionId, blob);
          } catch (err) {
            console.error("[Recording] upload failed:", err?.response?.data?.detail || err);
          }
        }
        resolve();
      };
      mr.stop();
    });
  }

  // ── Avatar ────────────────────────────────────────────────────────────────────
  const speakResponse = useCallback(async (text) => {
    if (!avatarRef.current || !text) return;
    setAvatarSpeaking(true);
    speechRef.current?.stop(); // mute mic while interviewer talks
    try { await avatarRef.current.speak(text); } finally {
      setAvatarSpeaking(false);
      lastActivityRef.current = Date.now();
      setSilencePrompt(false);
      // Auto-mic is handled by the useEffect below — no stale closure here
    }
  }, []);

  const onAvatarConnected = useCallback(() => {
    setAvatarReady(true);
    const last = [...messages].reverse().find((m) => m.role === "interviewer");
    if (last?.content) speakResponse(last.content);
    // speakResponse will auto-start mic when done
  }, [messages, speakResponse]);

  // ── Send ──────────────────────────────────────────────────────────────────────
  async function sendMessage(text, isHint = false) {
    if (!text.trim() && !isHint) return;
    // Prevent double-send: stop mic and disable auto-send before we proceed.
    // Without this, if the user typed + pressed Enter while the mic was still running,
    // rec.onend fires afterward and auto-sends the same transcript a second time.
    autoSendRef.current = false;
    try { speechRef.current?.stop(); } catch {}
    // Reset silence tracker on every send
    lastActivityRef.current = Date.now();
    setSilencePrompt(false);
    setSending(true);
    setMessages((p) => [...p, { role: "candidate", content: isHint ? "[Hint requested]" : text, stage, timestamp: new Date().toISOString() }]);
    setInput(""); setHintMode(false);
    avatarRef.current?.interrupt?.();
    try {
      const res = await api.mockSessionMessage(sessionId, isHint ? "" : text, isHint);
      setMessages((p) => [...p, { role: "interviewer", content: res.response, stage: res.current_stage, timestamp: new Date().toISOString() }]);
      if (res.stage_changed) setStage(res.current_stage);
      speakResponse(res.response);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
      setMessages((p) => p.slice(0, -1));
    } finally { setSending(false); }
  }

  // ── Resume ────────────────────────────────────────────────────────────────────
  async function handleResumeUpload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file);
    setUploading(true); avatarRef.current?.interrupt?.();
    try {
      const res = await api.mockSessionUploadResume(sessionId, form);
      setResumeUploaded(true); setStage(res.stage);
      setMessages((p) => [...p, { role: "interviewer", content: res.response, stage: res.stage, timestamp: new Date().toISOString() }]);
      toast.success("Resume uploaded!"); speakResponse(res.response);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  }

  // ── Code submit ───────────────────────────────────────────────────────────────
  async function handleCodeSubmit() {
    if (!code.trim() || code === "# Write your solution here\n") { toast.error("Write your solution first"); return; }
    setSubmitting(true); avatarRef.current?.interrupt?.();
    try {
      const res = await api.mockSessionCodeSubmit(sessionId, code, language);
      setMessages((p) => [...p, { role: "interviewer", content: res.interviewer_comment, stage: "coding_round", timestamp: new Date().toISOString() }]);
      setStage("wrap_up"); setShowCode(false);
      speakResponse(res.interviewer_comment);
      toast.success(res.evaluation?.passed ? "Code submitted!" : "Submitted — see feedback");
    } catch (err) { toast.error(err?.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  }

  // ── Voice ─────────────────────────────────────────────────────────────────────
  // autoSend=true → send immediately when speech ends (default interactive mode)
  // autoSend=false → just fill the text box (manual send via Enter/button)
  const autoSendRef = useRef(true);
  const [interimText, setInterimText] = useState(""); // live transcription preview

  function startVoice(autoSend = true) {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice not supported in this browser"); return; }
    // Check live conditions (not stale inputDisabled closure)
    if (sending || avatarSpeaking || stage === "completed" || stage === "resume_collection") return;

    autoSendRef.current = autoSend;

    const rec = new SR();
    rec.continuous        = true;   // keep listening until we explicitly stop
    rec.interimResults    = true;   // live transcription preview
    rec.maxAlternatives   = 1;
    // en-IN gives much better accuracy for Indian-English accents;
    // falls back gracefully to en-US on browsers that don't support it
    rec.lang              = "en-IN";

    let finalTranscript = "";
    let didAutoSend     = false;  // guard against duplicate fire on some browsers

    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          // Only keep the best alternative (index 0 has the highest confidence)
          finalTranscript += e.results[i][0].transcript + " ";
        } else {
          interim += e.results[i][0].transcript;
        }
      }
      setInterimText(interim);
      setInput(finalTranscript.trim());
    };

    rec.onerror = (e) => {
      if (e.error !== "no-speech") {
        toast.custom(() => (
          <div style={{
            background: "linear-gradient(135deg,#0a0f28,#12193d)",
            border: "1px solid rgba(230,57,70,0.45)",
            borderRadius: 12, padding: "14px 18px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 16px rgba(230,57,70,0.12)",
            color: "#fff", minWidth: 260,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ fontSize: 18 }}>🎙️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>Microphone Issue</div>
                <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, marginTop: 2 }}>
                  {e.error === "audio-capture" ? "Mic not detected — check your browser permissions." : `Error: ${e.error}`}
                </div>
              </div>
            </div>
          </div>
        ), { duration: 5000 });
      }
      setListening(false);
      setInterimText("");
    };

    rec.onend = () => {
      setListening(false);
      setInterimText("");
      const text = finalTranscript.trim();
      if (text && autoSendRef.current && !didAutoSend) {
        didAutoSend = true;      // prevent double-fire on browser restart quirks
        autoSendRef.current = false;
        setInput("");
        sendMessage(text);
      } else if (!text) {
        silenceRef.current = setTimeout(() => setHintMode(true), 4000);
      }
      finalTranscript = "";
    };

    speechRef.current = rec;
    rec.start();
    setListening(true);
    clearTimeout(silenceRef.current);
    lastActivityRef.current = Date.now();
    setSilencePrompt(false);
  }

  function stopVoice() {
    speechRef.current?.stop(); // triggers onend → auto-send if autoSend=true
    setListening(false);
  }

  // ── End ───────────────────────────────────────────────────────────────────────
  async function handleEnd() {
    if (!lockReleased) { toast.error("Locked for first 30 minutes"); return; }
    setEnding(true);
    try { avatarRef.current?.interrupt?.(); await stopRec(true); await api.mockSessionEnd(sessionId); navigate(`/mock/feedback/${sessionId}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not end session"); }
    finally { setEnding(false); }
  }

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (!session) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#060a1f" }}>
        <div style={{ color: "#e63946", fontFamily: "monospace", fontSize: 16 }}>Loading session…</div>
      </div>
    );
  }

  const personaName   = session.persona?.name   || "Interviewer";
  const personaGender = session.persona?.gender || "female";
  const isGeneral     = session.position_id === "general-practice";
  const interviewerRole = isGeneral ? "Technical Interviewer · Kiba Labs" : "Senior SAP Interviewer · Kiba Labs";

  // Deterministic random avatar per session
  const FEMALE_AVS = ["/fe-1.png", "/fe-2.png", "/fe-3.png", "/fe-4.png"];
  const MALE_AVS   = ["/male-1.png", "/male-2.png", "/male-3.png", "/male-4.png"];
  const pool       = personaGender === "female" ? FEMALE_AVS : MALE_AVS;
  const seed       = (session.id || sessionId || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const fallbackAv = pool[seed % pool.length];

  const inputDisabled = sending || avatarSpeaking || stage === "completed" || stage === "resume_collection";

  // Right video panel width
  const VIDEO_W = 300;

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#060a1f", color: "#f1f1f1", fontFamily: "'Inter',sans-serif", overflow: "hidden" }}>
      <style>{`
        @keyframes typing { 0%,80%,100%{opacity:0} 40%{opacity:1} }
        @keyframes wave { from{transform:scaleY(0.3)} to{transform:scaleY(1)} }
        @keyframes mic-pulse { 0%,100%{box-shadow:0 0 12px rgba(230,57,70,0.5)} 50%{box-shadow:0 0 28px rgba(230,57,70,0.9),0 0 50px rgba(230,57,70,0.3)} }
        @keyframes card-in { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .dot{display:inline-block;width:5px;height:5px;border-radius:50%;background:#e63946;margin:0 2px;animation:typing 1.2s infinite}
        .dot:nth-child(2){animation-delay:0.2s}.dot:nth-child(3){animation-delay:0.4s}
        *{box-sizing:border-box} textarea{resize:none;font-family:'Inter',sans-serif}
        ::-webkit-scrollbar{width:5px;background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:4px}
      `}</style>

      {/* ══ TOP BAR ══════════════════════════════════════════════════════════════ */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "0.45rem 1rem", flexShrink: 0,
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        background: "rgba(6,10,31,0.97)",
      }}>
        {/* Logo */}
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1, flexShrink: 0 }}>
          <span style={{ fontWeight: 900, fontSize: 16, letterSpacing: 1, color: "#fff", textTransform: "uppercase", fontFamily: "'Barlow Condensed','Impact',sans-serif" }}>
            PROCTORLY <span style={{ color: "#e63946" }}>AI</span>
          </span>
          <span style={{ fontSize: 8, letterSpacing: "0.16em", color: "rgba(255,255,255,0.3)", textTransform: "uppercase" }}>Powered by Kiba Labs</span>
        </div>
        <div style={{ width: 1, height: 26, background: "rgba(255,255,255,0.1)", flexShrink: 0 }} />
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, flexShrink: 0 }}>{STAGE_LABELS[stage]}</span>
        <StageProgress current={stage} />
        {!lockReleased && (
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 5, padding: "2px 8px", flexShrink: 0 }}>
            🔒 Nav locked
          </div>
        )}
        <button onClick={handleEnd} disabled={!lockReleased || ending} style={{
          background: lockReleased ? "rgba(230,57,70,0.15)" : "rgba(255,255,255,0.04)",
          border: `1px solid ${lockReleased ? "rgba(230,57,70,0.4)" : "rgba(255,255,255,0.08)"}`,
          color: lockReleased ? "#e63946" : "rgba(255,255,255,0.2)",
          borderRadius: 7, padding: "4px 13px", fontSize: 11, fontWeight: 600,
          cursor: lockReleased ? "pointer" : "not-allowed", flexShrink: 0,
        }}>{ending ? "Ending…" : lockReleased ? "End Interview" : "End (locked)"}</button>
      </div>

      {/* ══ BODY: Chat left + Videos right ═══════════════════════════════════════ */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* ── LEFT: Chat + Code Editor ── */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

          {/* Chat panel */}
          <div style={{
            flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
            borderRight: showCode ? "1px solid rgba(255,255,255,0.07)" : "none",
          }}>

            {/* ── Chat panel header: stage info + quick-action icons ── */}
            <div style={{
              flexShrink: 0, borderBottom: "1px solid rgba(255,255,255,0.07)",
              background: "rgba(8,12,35,0.90)", padding: "0.5rem 1rem",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              {/* Stage badge */}
              <div style={{
                background: "rgba(230,57,70,0.12)", border: "1px solid rgba(230,57,70,0.3)",
                borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700,
                color: "#e63946", letterSpacing: "0.05em", flexShrink: 0,
              }}>
                {STAGE_LABELS[stage] || stage}
              </div>

              {/* Messages count */}
              <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11, flexShrink: 0 }}>
                💬 {messages.length} messages
              </span>

              <div style={{ flex: 1 }} />

              {/* Resume upload shortcut — hidden input is rendered in the resume_collection section */}
                <button
                onClick={() => resumeInputRef.current?.click()}
                disabled={uploading || resumeUploaded}
                title={resumeUploaded ? "Resume uploaded ✓" : "Upload resume"}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  background: resumeUploaded ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.06)",
                  border: `1px solid ${resumeUploaded ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.12)"}`,
                  color: resumeUploaded ? "#22c55e" : "rgba(255,255,255,0.6)",
                  borderRadius: 7, padding: "4px 10px", fontSize: 11, fontWeight: 600,
                  cursor: resumeUploaded ? "default" : "pointer",
                }}
              >
                {resumeUploaded ? "✓ Resume" : uploading ? "Uploading…" : "📎 Resume"}
              </button>

              {/* Code editor toggle */}
              <button
                onClick={() => setShowCode((v) => !v)}
                title={showCode ? "Close code editor" : "Open code editor"}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  background: showCode ? "rgba(99,102,241,0.15)" : "rgba(255,255,255,0.06)",
                  border: `1px solid ${showCode ? "rgba(99,102,241,0.4)" : "rgba(255,255,255,0.12)"}`,
                  color: showCode ? "#a5b4fc" : "rgba(255,255,255,0.6)",
                  borderRadius: 7, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                }}
              >
                💻 {showCode ? "Close Editor" : "Code Editor"}
              </button>

              {/* Compact timer */}
              {session.lock_expires_at && (
                <SessionTimer startedAt={session.started_at} lockExpiresAt={session.lock_expires_at} maxExpiresAt={session.max_expires_at} onLockRelease={() => setLockReleased(true)} />
              )}
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.2rem" }}>
              {messages.map((m, i) => <ChatBubble key={i} msg={m} />)}
              {(sending || avatarSpeaking) && (
                <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
                  <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: "3px 14px 14px 14px", padding: "10px 14px" }}>
                    <span className="dot" /><span className="dot" /><span className="dot" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Always-mounted hidden file input (used by header button + resume_collection prompt) */}
            <input ref={resumeInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }} onChange={handleResumeUpload} />

            {/* Resume upload prompt — shown only when agent explicitly asks the user to upload their resume */}
            {!resumeUploaded && (() => {
              const lastInterviewerMsg = [...messages].reverse().find(m => m.role === "interviewer")?.content?.toLowerCase() || "";
              // Only match explicit upload requests — NOT casual mentions of "resume" in discussion
              const agentAskedForResume = lastInterviewerMsg.match(
                /\b(share|upload|send|attach|drop|provide|paste)\b.{0,30}\b(resume|cv)\b|\b(resume|cv)\b.{0,30}\b(share|upload|send|attach|drop|provide)\b/i
              );
              const shouldShow = stage === "resume_collection" || (stage === "warmup" && agentAskedForResume);
              if (!shouldShow) return null;
              return (
                <div style={{
                  margin: "0 1rem 0.75rem",
                  background: "linear-gradient(135deg, rgba(230,57,70,0.08), rgba(10,15,40,0.6))",
                  border: "1px solid rgba(230,57,70,0.35)",
                  borderRadius: 14, padding: "1rem 1.2rem",
                  boxShadow: "0 4px 20px rgba(230,57,70,0.1)",
                  backdropFilter: "blur(10px)",
                  animation: "card-in 0.3s ease-out",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 22 }}>📄</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13.5, color: "#f1f1f1" }}>Share Your Resume</div>
                      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, marginTop: 1 }}>
                        PDF or DOCX — helps tailor every question to your experience
                      </div>
                    </div>
                  </div>
                  <button onClick={() => resumeInputRef.current?.click()} disabled={uploading} style={{
                    width: "100%", padding: "9px 0",
                    background: "linear-gradient(90deg,#e63946,#c1121f)",
                    color: "#fff", border: "none", borderRadius: 9,
                    fontSize: 13, fontWeight: 700, cursor: uploading ? "wait" : "pointer",
                    boxShadow: "0 4px 16px rgba(230,57,70,0.4)",
                    letterSpacing: "0.04em",
                  }}>
                    {uploading ? "⏳ Uploading…" : "📎 Upload Resume Now"}
                  </button>
                </div>
              );
            })()}

            {/* Silence nudge — appears after 30 s of user inactivity */}
            {silencePrompt && !sending && !avatarSpeaking && (
              <div style={{
                margin: "0 1rem 0.6rem",
                background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.3)",
                borderRadius: 10, padding: "10px 14px",
              }}>
                <div style={{ color: "#a5b4fc", fontSize: 12, fontWeight: 700, marginBottom: 7 }}>
                  💭 {personaName}: &ldquo;Hey, just checking — can you still hear me okay? No rush at all, take your time.&rdquo;
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => { lastActivityRef.current = Date.now(); setSilencePrompt(false); }}
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.8)", borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>
                    I'm here, give me a moment
                  </button>
                  <button onClick={() => { setSilencePrompt(false); sendMessage("", true); }}
                    style={{ background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.35)", color: "#fbbf24", borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>
                    💡 I'd like a hint
                  </button>
                  <button onClick={() => { setSilencePrompt(false); setHintMode(true); }}
                    style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", color: "#22c55e", borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>
                    🔁 Rephrase the question
                  </button>
                </div>
              </div>
            )}

            {/* Hint banner */}
            {hintMode && (
              <div style={{ margin: "0 1rem 0.6rem", background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.22)", borderRadius: 9, padding: "8px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ color: "#fbbf24", fontSize: 13 }}>💡 Need a hint?</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => sendMessage("", true)} style={{ background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.35)", color: "#fbbf24", borderRadius: 6, padding: "3px 10px", fontSize: 12, cursor: "pointer" }}>Yes please</button>
                  <button onClick={() => setHintMode(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.3)", cursor: "pointer", fontSize: 13 }}>✕</button>
                </div>
              </div>
            )}

            {avatarSpeaking && (
              <div style={{ margin: "0 1rem 0.4rem", textAlign: "center", color: "rgba(230,57,70,0.65)", fontSize: 12 }}>
                Wait for {personaName} to finish speaking…
              </div>
            )}

            {/* Input bar */}
            <div style={{ padding: "0.4rem 1rem 0.6rem", borderTop: "1px solid rgba(255,255,255,0.07)", flexShrink: 0 }}>

              {/* Voice status indicator */}
              {listening && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                    {[0,1,2,3].map((i) => (
                      <div key={i} style={{
                        width: 3, borderRadius: 2, background: "#e63946",
                        animation: `wave 0.5s ease-in-out ${i * 0.1}s infinite alternate`,
                        height: `${8 + i * 4}px`,
                      }} />
                    ))}
                  </div>
                  <span style={{ color: "#e63946", fontSize: 11, fontWeight: 600, letterSpacing: "0.05em" }}>
                    Listening… {interimText && <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 400 }}>"{interimText}"</span>}
                  </span>
                  <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 10, marginLeft: "auto" }}>Stop speaking to auto-send</span>
                </div>
              )}

              <div style={{ display: "flex", gap: 7, alignItems: "flex-end" }}>
                {/* Text input — secondary, for typing fallback */}
                <textarea
                  value={input}
                  onChange={(e) => { setInput(e.target.value); lastActivityRef.current = Date.now(); setSilencePrompt(false); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (!listening) sendMessage(input); } }}
                  disabled={inputDisabled || listening}
                  placeholder={
                    avatarSpeaking ? "Interviewer is speaking…" :
                    listening ? "Listening — speak your answer…" :
                    stage === "resume_collection" ? "Upload resume first…" :
                    stage === "completed" ? "Interview completed" :
                    "Speak 🎤 or type your answer (Enter to send)"
                  }
                  style={{
                    flex: 1, background: listening ? "rgba(230,57,70,0.05)" : "rgba(255,255,255,0.05)",
                    border: `1px solid ${listening ? "rgba(230,57,70,0.25)" : inputDisabled ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.12)"}`,
                    borderRadius: 9, padding: "9px 12px", color: listening ? "rgba(255,255,255,0.4)" : "#f1f1f1",
                    fontSize: 13.5, minHeight: 42, maxHeight: 110, lineHeight: 1.5,
                    opacity: inputDisabled && !listening ? 0.5 : 1,
                  }} rows={1}
                />

                {/* Mic — primary action, glows red when active */}
                <button
                  onClick={listening ? stopVoice : () => startVoice(true)}
                  disabled={inputDisabled}
                  title={listening ? "Stop recording (auto-sends)" : "Start speaking (auto-sends when done)"}
                  style={{
                    width: 46, height: 46, borderRadius: 10, border: "none", flexShrink: 0,
                    background: listening ? "linear-gradient(135deg,#e63946,#c1121f)" : avatarSpeaking ? "rgba(255,255,255,0.04)" : "rgba(230,57,70,0.15)",
                    border: listening ? "none" : "1px solid rgba(230,57,70,0.35)",
                    color: "#fff", cursor: inputDisabled ? "not-allowed" : "pointer", fontSize: 19,
                    boxShadow: listening ? "0 0 18px rgba(230,57,70,0.6), 0 0 40px rgba(230,57,70,0.2)" : "none",
                    opacity: inputDisabled ? 0.35 : 1,
                    transition: "all 0.2s",
                  }}>
                  {listening ? "⏹" : "🎤"}
                </button>

                {/* Code editor toggle */}
                <button onClick={() => setShowCode((v) => !v)} title={showCode ? "Close code editor" : "Open code editor"}
                  style={{ width: 42, height: 42, borderRadius: 9, border: "none", flexShrink: 0, background: showCode ? "linear-gradient(135deg,#6366f1,#4f46e5)" : "rgba(255,255,255,0.07)", color: "#fff", cursor: "pointer", fontSize: 17, boxShadow: showCode ? "0 0 14px rgba(99,102,241,0.4)" : "none" }}>
                  💻
                </button>

                {/* Send — only useful when NOT in voice mode */}
                <button onClick={() => { stopVoice(); sendMessage(input); }} disabled={inputDisabled || (!input.trim() && !listening)}
                  style={{ height: 42, borderRadius: 9, border: "none", paddingInline: 16, flexShrink: 0, background: (!inputDisabled && input.trim()) ? "linear-gradient(135deg,#e63946,#c1121f)" : "rgba(255,255,255,0.07)", color: "#fff", cursor: (!inputDisabled && input.trim()) ? "pointer" : "not-allowed", fontSize: 17, opacity: (!inputDisabled && input.trim()) ? 1 : 0.3 }}>
                  ↑
                </button>
              </div>
            </div>
          </div>

          {/* Code editor — slides in alongside chat */}
          {showCode && (
            <div style={{ width: "44%", display: "flex", flexDirection: "column", flexShrink: 0, overflow: "hidden" }}>
              <div style={{ padding: "0.55rem 1rem", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(10,14,40,0.8)", flexShrink: 0 }}>
                <span style={{ color: "#a5b4fc", fontWeight: 700, fontSize: 13 }}>💻 Code Editor</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select value={language} onChange={(e) => setLanguage(e.target.value)}
                    style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "#f1f1f1", borderRadius: 6, padding: "4px 8px", fontSize: 12 }}>
                    <option value="python">Python</option>
                    <option value="java">Java</option>
                    <option value="javascript">JavaScript</option>
                    <option value="typescript">TypeScript</option>
                    <option value="shell">Shell / Bash</option>
                    <option value="groovy">Groovy</option>
                  </select>
                  <button onClick={handleCodeSubmit} disabled={submitting || stage === "wrap_up"}
                    style={{ background: submitting || stage === "wrap_up" ? "rgba(255,255,255,0.07)" : "linear-gradient(135deg,#22c55e,#16a34a)", color: "#fff", border: "none", borderRadius: 8, padding: "5px 13px", fontWeight: 700, fontSize: 12, cursor: submitting || stage === "wrap_up" ? "not-allowed" : "pointer" }}>
                    {submitting ? "Submitting…" : stage === "wrap_up" ? "Submitted ✓" : "Submit Code"}
                  </button>
                  <button onClick={() => setShowCode(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>✕</button>
                </div>
              </div>
              <div style={{ flex: 1, overflow: "hidden" }}>
                <Editor height="100%" language={language === "shell" ? "bash" : language === "groovy" ? "java" : language}
                  value={code} onChange={(v) => setCode(v || "")} theme="vs-dark"
                  options={{ fontSize: 13.5, lineNumbers: "on", minimap: { enabled: false }, scrollBeyondLastLine: false, wordWrap: "on", padding: { top: 10, bottom: 10 }, tabSize: 4 }}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: Videos stacked ── */}
        <div style={{
          width: VIDEO_W, flexShrink: 0, display: "flex", flexDirection: "column",
          borderLeft: "1px solid rgba(255,255,255,0.08)",
        }}>
          {/* Top: Interviewer */}
          <InterviewerPanel
            avatarRef={avatarRef}
            sessionId={sessionId}
            gender={personaGender}
            name={personaName}
            fallbackSrc={fallbackAv}
            role={interviewerRole}
            speaking={avatarSpeaking}
            ready={avatarReady}
            onConnected={onAvatarConnected}
            onSpeakStart={() => setAvatarSpeaking(true)}
            onSpeakEnd={() => setAvatarSpeaking(false)}
          />
          {/* Bottom: User webcam */}
          <UserCamPanel stream={camStream} />
        </div>
      </div>
    </div>
  );
}
